/**
 * 用于触发生成文档推荐标题任务
 */
import {useEffect, useRef} from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';
import {throttle} from 'lodash-es';

import sdk from '@/sdk';
import * as store from '@/store';
import {fetchDocToken} from '@/services';
import {ServerConfig} from '@/config/routers';
import {EDITOR_MODE} from '@/Constant';
import eventBus, {EDITOR_FS_DOM_READY} from '@/eventBus';
import {initGray, GRAY_RELEASE_KEYS} from '@/grayRelease';

const {
    useIsInGray: useIsInGrayAiRecommendTitle,
} = initGray(GRAY_RELEASE_KEYS.aiRecommendTitle);

/**
 * 用于触发生成文档推荐标题任务的websocket连接
 */
class RecommendTitleWebSocket {
    socket: ReconnectingWebSocket | null = null;
    docGuid: string;

    constructor(docGuid: string) {
        this.docGuid = docGuid;
    }

    async urlProvider() {
        const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
        return `${ServerConfig.kuWsOrigin}/wiki/ai/ws/assist?token=${rteToken}`;
    }

    send() {
        this.socket = new ReconnectingWebSocket(this.urlProvider.bind(this), [], {
            maxRetries: 0,
            maxReconnectionDelay: 2000,
        });
        this.socket.addEventListener('open', () => {
            this.socket?.send(JSON.stringify({
                conversationId: `generate-title:${sdk.user?.userGuid!}:${this.docGuid}`,
                context: {
                    action: 'recommendTitle',
                },
            }));

            setTimeout(() => {
                this.socket?.close();
            }, 1000);
        });
        this.socket?.addEventListener('message', (event) => {
            const data = JSON.parse(event.data);
            // token失效，更新token
            if (data.code === 402) {
                const repository = store.getCurRepository();
                const curDoc = store.getCurDocument();
                if (repository && curDoc) {
                    fetchDocToken(repository, curDoc.docGuid);
                }
            }
        });
    }
}

/**
 * 触发推荐标题任务的 Hook
 * @param docGuid 文档 ID
 * @param mode 文档模式
 * @param docReady 文档渲染完成
 */
const useRecommendTitleTask = (docGuid: string, mode: EDITOR_MODE) => {
    const isEditOrMeetingMode = [EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(mode);
    const wordNumRef = useRef(0);
    const isInGray = useIsInGrayAiRecommendTitle();

    function triggerTask() {
        if (!isInGray) {
            return;
        }
        const {current: editor} = store.getEditorRef();
        if (!editor || !editor.getDocLength || !isEditOrMeetingMode) {
            return;
        }
        const wordNum = editor.getDocLength({type: 'word'}) || 0;
        // 当文档内容变更大于一定字数时触发一次任务
        if (Math.abs(wordNum - wordNumRef.current) > 100) {
            const ws = new RecommendTitleWebSocket(docGuid);
            ws.send();
            wordNumRef.current = wordNum;
        }
    }
    // triggerTask节流
    const triggerTaskRef = useRef(triggerTask);
    triggerTaskRef.current = triggerTask;
    const throttledTriggerTaskRef = useRef(throttle(() => {
        triggerTaskRef.current();
    }, 1000 * 15));

    // 初始触发一次
    useEffect(() => {
        function onReady() {
            triggerTaskRef.current();
        }
        eventBus.on(EDITOR_FS_DOM_READY, onReady);

        return () => {
            eventBus.off(EDITOR_FS_DOM_READY, onReady);
        };
    }, []);

    return {triggerTask: throttledTriggerTaskRef.current};
};

export default useRecommendTitleTask;
